import React from "react";
import AppModal from "../../../../components/AppModal/AppModal";
import {
  Box,
  Grid,
  Typography,
  Chip,
  Paper,
  Divider,
} from "@mui/material";
import { format } from "date-fns";

interface ReferenceViewModalProps {
  open: boolean;
  onClose: () => void;
  data: any;
}

const ReferenceViewModal: React.FC<ReferenceViewModalProps> = ({
  open,
  onClose,
  data,
}) => {
  if (!data) return null;

  const formatDate = (date: string) =>
    date ? format(new Date(date), "dd/MM/yyyy") : "N/A";

  const renderRating = (value: number | string | undefined) => {
    if (value === undefined || value === null) return "N/A";
    const num = typeof value === "string" ? parseInt(value, 10) : value;
    const map: Record<number, string> = {
      0: "Excellent",
      1: "Good",
      2: "Average",
      3: "Poor",
    };
    return map[num] || num;
  };

  const renderBoolean = (value: boolean | string | undefined) => {
    if (value === undefined || value === null) return "N/A";
    const bool = typeof value === "string" ? value === "true" : value;
    return bool ? (
      <Chip label="Yes" color="success" size="small" />
    ) : (
      <Chip label="No" color="error" size="small" />
    );
  };

  return (
    <AppModal
      title={`Reference Form – ${data.candidateName || data.workerName || ""}`}
      open={open}
      onCancel={onClose}
      width={800}
      footer={null}
    >
      <Box sx={{ maxHeight: "70vh", overflowY: "auto", px: 1 }}>
        <Typography variant="h6" gutterBottom>
          Candidate & Referee Information
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Candidate Name
              </Typography>
              <Typography variant="body1">{data.candidateName || "N/A"}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Trade/Role
              </Typography>
              <Typography variant="body1">{data.tradeRole || "N/A"}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Company Worked For
              </Typography>
              <Typography variant="body1">{data.companyWorkedFor || "N/A"}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Referee Name
              </Typography>
              <Typography variant="body1">{data.refereeName || "N/A"}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Referee Position
              </Typography>
              <Typography variant="body1">{data.refereePosition || "N/A"}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Contact Number
              </Typography>
              <Typography variant="body1">{data.contactNumber || "N/A"}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Email Address
              </Typography>
              <Typography variant="body1">{data.emailAddress || "N/A"}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                From Date
              </Typography>
              <Typography variant="body1">{formatDate(data.dateFrom)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                To Date
              </Typography>
              <Typography variant="body1">{formatDate(data.dateTo)}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Site/Project
              </Typography>
              <Typography variant="body1">{data.siteProject || "N/A"}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Reason for Leaving
              </Typography>
              <Typography variant="body1">{data.reasonForLeaving || "N/A"}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="h6" gutterBottom>
          Reference Questions
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                1. What duties did the candidate carry out?
              </Typography>
              <Typography variant="body1">{data.dutiesCarriedOut || "N/A"}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                2. How would you rate their timekeeping and attendance?
              </Typography>
              <Typography variant="body1">
                {renderRating(data.timekeepingAndAttendance)}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Comments (Timekeeping)
              </Typography>
              <Typography variant="body1">{data.timekeepingComments || "N/A"}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                3. Attitude and work ethic
              </Typography>
              <Typography variant="body1">{data.attitudeAndWorkEthic || "N/A"}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                4. Follow site rules, H&S, instructions?
              </Typography>
              <Box>{renderBoolean(data.followedSiteRules)}</Box>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Comments (Site Rules)
              </Typography>
              <Typography variant="body1">{data.siteRulesComments || "N/A"}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                5. Rate quality of work
              </Typography>
              <Typography variant="body1">{renderRating(data.qualityOfWork)}</Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                6. Reliable and suitable for site work?
              </Typography>
              <Box>{renderBoolean(data.reliableForSiteWork)}</Box>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                7. Would you re-employ?
              </Typography>
              <Box>{renderBoolean(data.wouldReemploy)}</Box>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" color="textSecondary">
                Comments (Re-employ)
              </Typography>
              <Typography variant="body1">{data.reemployComments || "N/A"}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Typography variant="h6" gutterBottom>
          Overall Reference Summary
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="body1">
            {data.overallReferenceSummary || "N/A"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Completed By
              </Typography>
              <Typography variant="body1">
                {data.completedByUserFullName || "N/A"}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Completion Date
              </Typography>
              <Typography variant="body1">
                {formatDate(data.completionDate)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </AppModal>
  );
};

export default ReferenceViewModal;
